import { useState, useEffect, useCallback, useRef } from "react";
import { AlertCircle, Check, ChevronDown, RefreshCw } from "lucide-react";
import host from "../../../../../global";
import { useTheme } from "../../../../../hooks/useTheme";
import SubStepBreadcrumb from "./SubStepBreadcrumb";
import SkeletonCard from "./components/SkeletonCard";

// Per-skill topic selection (Step 2 → Requirements substep).
// All skills are pre-fetched in parallel on mount. AbortControllers and a
// functional state updater prevent race conditions across concurrent fetches.
// Custom-added topics are pinned above AI suggestions so they stay visible.
// During regen, selected cards remain visible; unselected slots show skeletons.

// Dark-mode card gradient shared by TopicCard and CustomInputSection
const DARK_CARD_GRADIENT =
  "linear-gradient(233.57deg, rgba(217, 217, 217, 0.05) -5.01%, rgba(115, 108, 207, 0.01) 100%)";


// ─── Sub-components ───────────────────────────────────────────────────────────

const LoadingIndicator = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
    {Array.from({ length: 9 }, (_, i) => (
      <SkeletonCard key={i} lines={2} height="64px" />
    ))}
  </div>
);

const TopicCard = ({ topicText, isSelected, isDisabled, isReadonly, theme, onClick }) => (
  <button
    type="button"
    onClick={isReadonly ? undefined : onClick}
    disabled={isDisabled || isReadonly}
    style={{ backgroundImage: theme === "dark" ? DARK_CARD_GRADIENT : undefined }}
    className={`p-3 h-16 flex items-center text-left transition-colors
      backdrop-blur-sm border rounded-lg dark:shadow-lg shadow-sm
      ${isSelected
        ? "dark:bg-purple-500/20 border-purple-400 dark:text-[#CCCCCC] text-textPrimary bg-bgSecondary"
        : isDisabled
        ? "dark:bg-gray-800/30 bg-gray-200 dark:text-gray-500 text-textSecondary border-gray-700/50 cursor-not-allowed opacity-50"
        : "dark:text-[#CCCCCC] text-textSecondary border-gray-700/50 dark:hover:bg-gray-600/40 hover:bg-gray-200 cursor-pointer"
      }`}
  >
    <div className="flex items-center justify-between w-full gap-2">
      <span className="dark:text-[#CCCCCC] text-textSecondary text-sm line-clamp-2">
        {topicText}
      </span>
      {isSelected && (
        <div className="h-5 w-5 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex justify-center items-center flex-shrink-0">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}
    </div>
  </button>
);

const CustomInputSection = ({
  skill, expandedSkill, showCustomInput, customInput, isReadonly, theme,
  selectedTopics, maxTopics, customInputRef,
  onStateUpdate, handleAddCustomTopic, handleCancelCustomInput, handleCustomInputKeyDown,
}) => {
  const currentTopics = selectedTopics[skill] || [];
  const atMax = currentTopics.length >= maxTopics;

  if (!showCustomInput || expandedSkill !== skill) {
    return (
      <button
        onClick={isReadonly ? undefined : () => onStateUpdate({ showCustomInput: true, error: null, customInput: "" })}
        disabled={atMax || isReadonly}
        style={{ backgroundImage: theme === "dark" ? DARK_CARD_GRADIENT : undefined }}
        className={`w-full mt-4 p-4 rounded-lg text-left transition-colors border border-gray-700/50
          backdrop-blur-lg dark:shadow-lg shadow-sm
          ${atMax || isReadonly
            ? "dark:bg-gray-800/30 bg-bgPrimary dark:text-gray-500 text-textSecondary cursor-not-allowed opacity-50"
            : "dark:bg-gray-700/30 dark:text-gray-300 bg-bgPrimary text-textSecondary hover:bg-gray-200 cursor-pointer"
          }`}
      >
        <span className="font-medium text-sm">+ Add Custom</span>
      </button>
    );
  }

  return (
    <div
      style={{
        backgroundImage: theme === "dark"
          ? "linear-gradient(227.26deg, rgba(115, 115, 115, 0.5) 7.8%, rgba(61, 61, 61, 0.2) 109.11%)"
          : undefined,
        border: "1px solid rgba(138, 43, 226, 0.5)",
      }}
      className="p-3 mt-4 rounded-lg backdrop-blur-lg dark:shadow-lg shadow-sm relative z-10"
    >
      <div className="space-y-3">
        <input
          ref={customInputRef}
          type="text"
          value={customInput}
          onChange={isReadonly ? undefined : (e) => onStateUpdate({ customInput: e.target.value, error: null })}
          onKeyDown={isReadonly ? undefined : handleCustomInputKeyDown}
          placeholder={`+Add custom topic for ${skill}`}
          className={`w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded-lg
            dark:text-white text-textSecondary placeholder-gray-400
            focus:outline-none focus:ring-2 dark:focus:ring-gray-200 focus:ring-gray-400 focus:border-transparent
            ${isReadonly ? "opacity-50 cursor-not-allowed" : ""}`}
          maxLength={100}
          autoFocus={!isReadonly}
          spellCheck="false"
          autoComplete="off"
          disabled={isReadonly}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs dark:text-gray-400 text-gray-600">
            {customInput.length}/100 characters
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={isReadonly ? undefined : handleCancelCustomInput}
              disabled={isReadonly}
              className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={isReadonly ? undefined : handleAddCustomTopic}
              disabled={!customInput.trim() || isReadonly}
              className="px-4 py-1.5 bg-gradient-to-r from-pink-500 to-blue-500
                disabled:bg-gray-600 disabled:cursor-not-allowed
                text-white rounded-full text-sm font-medium transition-colors"
            >
              Add Topic
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── Main component ───────────────────────────────────────────────────────────

const CustomStep2d_Requirements = ({
  skills,
  selectedTopics,
  suggestions,
  loadingSkills,
  error,
  showCustomInput,
  customInput,
  maxTopicsPerSkill = {},

  onTopicToggle,
  onCustomTopicAdd,
  onStateUpdate,
  onStateUpdateFn,
  accessToken,
  round,
  jobDescription,
  isReadonly = false,
  experienceLevel,
  allFocusAreas = [],
  onSubStepNav,
}) => {
  const { theme } = useTheme();
  const [expandedSkill, setExpandedSkill] = useState(null);

  // Per-skill AbortControllers — cancel in-flight fetches on unmount or re-fetch
  const abortControllersRef = useRef(new Map());
  // Tracks which skills have already been fetched — guards against duplicate fetches on re-render
  const fetchedSkillsRef = useRef(new Set());
  // Always holds the latest generateSuggestions — lets the pre-fetch effect call it without
  // adding it to deps (which would cause infinite loops via useCallback recreation)
  const generateSuggestionsRef = useRef(null);
  // Stable ref for maxTopicsPerSkill — avoids recreating generateSuggestions on every render
  const maxTopicsPerSkillRef = useRef(maxTopicsPerSkill);
  const customInputRef = useRef(null);


  // ── Effects ────────────────────────────────────────────────────────────────

  // Mount: clear fetch tracking so this mount starts fresh.
  // Unmount: cancel all in-flight requests.
  // In React StrictMode (dev only) this fires mount→unmount→mount; clearing on
  // mount ensures the second real mount re-fetches after StrictMode aborts the first.
  useEffect(() => {
    fetchedSkillsRef.current.clear();
    return () => {
      abortControllersRef.current.forEach((ctrl) => ctrl.abort());
      abortControllersRef.current.clear();
    };
  }, []);

  // Sync refs every render so async callbacks always read the latest value.
  // generateSuggestions is defined below — its ref is set again after that.
  generateSuggestionsRef.current = null;
  maxTopicsPerSkillRef.current = maxTopicsPerSkill;

  // ESC closes the custom topic input panel (unless focus is already inside an input)
  useEffect(() => {
    const onKeydown = (e) => {
      if (e.key === "Escape" && e.target.tagName !== "TEXTAREA" && e.target.tagName !== "INPUT") {
        onStateUpdate({ showCustomInput: false, customInput: "", error: null });
      }
    };
    document.addEventListener("keydown", onKeydown);
    return () => document.removeEventListener("keydown", onKeydown);
  }, [onStateUpdate]);

  // Pre-fetch all skills in parallel whenever the skill list changes.
  // Uses generateSuggestionsRef so this effect never needs to list generateSuggestions as a dep.
  useEffect(() => {
    if (!skills.length) return;
    setExpandedSkill((prev) => prev ?? skills[0]);
    skills.forEach((skill) => {
      if (!fetchedSkillsRef.current.has(skill)) {
        fetchedSkillsRef.current.add(skill);
        generateSuggestionsRef.current?.(skill);
      }
    });
  }, [skills]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── AI fetch ───────────────────────────────────────────────────────────────

  // currentSelections passed as a param (not read from closure) to keep selectedTopics
  // out of the dependency array and avoid infinite re-creation loops.
  const generateSuggestions = useCallback(
    async (skill, customPrompt = "", currentSelections = []) => {
      if (!skill || !round) return;

      // Cancel any existing in-flight request for this skill before starting a new one
      const existing = abortControllersRef.current.get(skill);
      if (existing) existing.abort();
      const controller = new AbortController();
      abortControllersRef.current.set(skill, controller);

      onStateUpdateFn((prev) => ({
        loadingSkills: { ...prev.loadingSkills, [skill]: true },
        error: null,
      }));

      try {
        const otherFocusAreas = allFocusAreas.filter(
          (area) => area.toLowerCase() !== skill.toLowerCase()
        );
        const avoidTopics =
          currentSelections.length > 0 || otherFocusAreas.length > 0
            ? [...otherFocusAreas, ...currentSelections]
            : null;

        // Derive how many suggestions to show: selectable max + 3 buffer, clamped to [6, 12]
        const maxForSkill = maxTopicsPerSkillRef.current[skill] || 2;
        const suggestionsToFetch = Math.min(12, Math.max(6, maxForSkill + 3));

        const response = await fetch(`${host}/api/generate-comprehensive-suggestions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ai_question: `Generate ${suggestionsToFetch} subtopics for ${skill}`,
            custom_prompt: customPrompt || "",
            conversation_context: [],
            round_details: { type: round.type, duration: round.duration, focus: skill },
            job_description: jobDescription,
            avoid_topics: avoidTopics,
            selected_topics: currentSelections,
            is_regeneration: Boolean(customPrompt) || Boolean(avoidTopics),
            extended_thinking: false,
            experience_level: experienceLevel,
          }),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const data = await response.json();
        if (!data.suggestions?.length) throw new Error("No suggestions received");

        const newSuggestions = data.suggestions.slice(0, suggestionsToFetch);

        // Auto-select must_have topics only on first load (no existing selections)
        onStateUpdateFn((prev) => {
          const alreadySelected = prev.selectedTopics[skill] || [];
          const maxForSkill = maxTopicsPerSkillRef.current[skill] || 2;
          const autoSelect =
            alreadySelected.length === 0
              ? newSuggestions
                  .filter((s) => s.priority === "must_have")
                  .map((s) => s.title || s.text || "")
                  .filter(Boolean)
                  .slice(0, maxForSkill)
              : [];

          return {
            suggestions: { ...prev.suggestions, [skill]: newSuggestions },
            selectedTopics: autoSelect.length > 0
              ? { ...prev.selectedTopics, [skill]: autoSelect }
              : prev.selectedTopics,
            loadingSkills: { ...prev.loadingSkills, [skill]: false },
            error: null,
          };
        });

      } catch (err) {
        if (err.name === "AbortError") return;
        fetchedSkillsRef.current.delete(skill); // allow retry via Regenerate
        onStateUpdateFn((prev) => ({
          loadingSkills: { ...prev.loadingSkills, [skill]: false },
          error: `Generation failed: ${err.message}`,
        }));
      } finally {
        abortControllersRef.current.delete(skill);
      }
    },
    [round, jobDescription, onStateUpdateFn, experienceLevel, allFocusAreas, accessToken]
  );

  // Set ref now that generateSuggestions is defined
  generateSuggestionsRef.current = generateSuggestions;


  // ── Custom topic handlers ──────────────────────────────────────────────────

  const handleAddCustomTopic = useCallback(() => {
    const topic = customInput?.trim();
    if (!topic || !expandedSkill) return;
    onCustomTopicAdd(expandedSkill, topic, maxTopicsPerSkill[expandedSkill] || 2);
    onStateUpdate({ showCustomInput: false, customInput: "", error: null });
  }, [expandedSkill, customInput, maxTopicsPerSkill, onCustomTopicAdd, onStateUpdate]);

  const handleCancelCustomInput = useCallback(() => {
    onStateUpdate({ showCustomInput: false, customInput: "", error: null });
  }, [onStateUpdate]);

  const handleCustomInputKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleAddCustomTopic();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancelCustomInput();
    }
  }, [handleAddCustomTopic, handleCancelCustomInput]);


  // ── Render ─────────────────────────────────────────────────────────────────

  if (!round || !skills.length) return null;

  return (
    <div className="space-y-6 mb-10">

      <SubStepBreadcrumb activeIndex={3} onStepClick={onSubStepNav} />

      {error && (
        <div className="mb-4 bg-red-900/20 border border-red-600/30 rounded-lg p-3 flex items-center space-x-3">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {skills.map((skill) => {
          const isOpen = expandedSkill === skill;
          const skillTopics = selectedTopics[skill] || [];
          const skillSuggestions = suggestions[skill] || [];
          const isSkillLoading = loadingSkills[skill] || false;
          const maxForSkill = maxTopicsPerSkill[skill] || 2;
          const pct = Math.round((skillTopics.length / maxForSkill) * 100);

          // Custom-added topics are not in the AI suggestions list — pin them at the top
          const suggestionTitles = new Set(skillSuggestions.map((s) => s.title || s.text || ""));
          const pinnedCustomTopics = skillTopics.filter((t) => !suggestionTitles.has(t));

          return (
            <div
              key={skill}
              className={`rounded-lg border overflow-hidden transition-all duration-200 ${
                isOpen
                  ? "dark:border-gray-700/50 border-gray-200"
                  : skillTopics.length > 0
                  ? "dark:border-green-700/40 border-green-300"
                  : "dark:border-gray-700/50 border-gray-200"
              }`}
            >
              {/* Accordion header */}
              <button
                onClick={() => setExpandedSkill(isOpen ? null : skill)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                  theme === "dark"
                    ? isOpen ? "bg-transparent"   : "bg-gray-800/40 hover:bg-gray-700/40"
                    : isOpen ? "bg-transparent"   : "bg-bgPrimary hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium dark:text-white text-textPrimary">{skill}</span>
                  <span className="text-xs dark:text-green-400 text-green-600">
                    {skillTopics.length > 0 ? `${skillTopics.length}/${maxForSkill} topics` : `0/${maxForSkill} topics`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-1.5 rounded-full dark:bg-gray-700 bg-gray-200">
                    <div
                      className="h-1.5 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 dark:text-gray-400 text-gray-500 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {/* Expanded panel */}
              {isOpen && (
                <div className="px-4 pb-4 pt-3 space-y-4">
                  <div className="border-t dark:border-gray-700 border-gray-200 -mx-4" />
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-medium dark:text-gray-300 text-textSecondary">
                      AI Suggestions:
                    </h5>
                    {!isReadonly && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchedSkillsRef.current.delete(skill);
                          // Avoid all currently shown titles so AI generates fresh topics
                          const shownTitles = skillSuggestions
                            .map((s) => s.title || s.text || "")
                            .filter(Boolean);
                          generateSuggestions(skill, "", shownTitles);
                        }}
                        disabled={isSkillLoading}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-full
                          dark:bg-gray-700 bg-gray-100 dark:text-gray-300 text-textSecondary
                          hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                      >
                        <RefreshCw className={`w-3 h-3 ${isSkillLoading ? "animate-spin" : ""}`} />
                        Regenerate
                      </button>
                    )}
                  </div>

                  {isSkillLoading && !skillSuggestions.length ? (
                    <LoadingIndicator />
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">

                        {/* Pinned custom topics */}
                        {pinnedCustomTopics.map((t) => (
                          <TopicCard
                            key={`custom-${t}`}
                            topicText={t}
                            isSelected
                            isDisabled={false}
                            isReadonly={isReadonly}
                            theme={theme}
                            onClick={() => onTopicToggle(skill, t, maxForSkill)}
                          />
                        ))}

                        {/* AI suggestions — unselected slots show skeletons during regen */}
                        {skillSuggestions.map((suggestion, index) => {
                          const topicText = suggestion.title || suggestion.text || `Topic ${index + 1}`;
                          const isSelected = skillTopics.includes(topicText);
                          const isDisabled = !isSelected && skillTopics.length >= maxForSkill;

                          if (isSkillLoading && !isSelected) {
                            return <SkeletonCard key={suggestion.id || index} lines={2} height="64px" />;
                          }

                          return (
                            <TopicCard
                              key={suggestion.id || index}
                              topicText={topicText}
                              isSelected={isSelected}
                              isDisabled={isDisabled}
                              isReadonly={isReadonly}
                              theme={theme}
                              onClick={() => !isDisabled && onTopicToggle(skill, topicText, maxForSkill)}
                            />
                          );
                        })}
                      </div>

                      <CustomInputSection
                        skill={skill}
                        expandedSkill={expandedSkill}
                        showCustomInput={showCustomInput}
                        customInput={customInput}
                        isReadonly={isReadonly}
                        theme={theme}
                        selectedTopics={selectedTopics}
                        maxTopics={maxForSkill}
                        customInputRef={customInputRef}
                        onStateUpdate={onStateUpdate}
                        handleAddCustomTopic={handleAddCustomTopic}
                        handleCancelCustomInput={handleCancelCustomInput}
                        handleCustomInputKeyDown={handleCustomInputKeyDown}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default CustomStep2d_Requirements;
